import React, { useState, useRef, useEffect, useLayoutEffect, useMemo, useCallback } from "react";
import { createPortal } from "react-dom";
import moment from "moment";
import UpdateSidebar from "../sidebars/updateSidebar";
import { useCopyLaneToDay, useDeleteLane, useLaneByPlanID, useLanesByDay } from "../hooks/useLanes";
import CopyMoveLaneSidebar from "../sidebars/copyMoveLaneSidebar";
import { ReactComponent as AlertIcon } from "images/alert.svg";
import { ReactComponent as ArrowLeftIcon } from "images/arrow_left.svg";
import { ReactComponent as ArrowRightIcon } from "images/arrow_right.svg";
import AddDropModal from "./addDropModal";
import ConfirmationModal from "ui-components/confirmation-modal/confirmationModal";
import { setPlanId, staticLoadSelector } from "slices/static-load/staticLoadSlice";
import { useDispatch, useSelector } from "react-redux";
import { transformLanesToRows } from "../utils/transformLanes";
import { useTerminalDetailsByNumbers } from "../hooks/useTerminalDetailsByNumbers";
import { formatTerminalsByNumber } from "../utils/utils";
import Loading from "ui-components/loading/loading";
import { ReactComponent as ThreeDotsIcon } from "images/three_dots.svg";
import { useWeeks } from "../hooks/useWeek";

import "./dispatchBoard.scss";
const DAY_COLUMN_COUNT = 7;
const FLOATING_GAP = 6;
const VIEWPORT_PADDING = 8;

const computeFloatingPosition = (
  anchorRect: DOMRect,
  floatingWidth: number,
  floatingHeight: number
) => {
  const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PADDING;
  const spaceAbove = anchorRect.top - VIEWPORT_PADDING;

  let top: number;
  if (spaceBelow >= floatingHeight + FLOATING_GAP) {
    top = anchorRect.bottom + FLOATING_GAP;
  } else if (spaceAbove >= floatingHeight + FLOATING_GAP) {
    top = anchorRect.top - floatingHeight - FLOATING_GAP;
  } else if (spaceBelow >= spaceAbove) {
    top = Math.max(
      VIEWPORT_PADDING,
      window.innerHeight - floatingHeight - VIEWPORT_PADDING
    );
  } else {
    top = VIEWPORT_PADDING;
  }

  let left = anchorRect.left;
  if (left + floatingWidth > window.innerWidth - VIEWPORT_PADDING) {
    left = window.innerWidth - floatingWidth - VIEWPORT_PADDING;
  }
  left = Math.max(VIEWPORT_PADDING, left);

  return { top, left };
};

// ─── Types ───────────────────────────────────────────────────────────────────

export interface Dispatch {
  id: string;
  airport: string;
  destinationName?: string;
  origin?: string;
  time: string;
  carrier?: string;
  price?: string;
  color?: "blue" | "purple" | "green";
  driverInitial?: string;
  driverColor?: string;
  drops?: number;
  carrierRate?: string;
}

export interface Lane {
  origin: string;
  destination: string;
  departure: string;
  carrier: string;
  drops: number;
}

export interface Row {
  label: string;
  originCode: string;
  dispatches: (Dispatch | null)[];
}

// ─── Tooltip ─────────────────────────────────────────────────────────────────

interface TooltipProps {
  lane: Lane;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  onClose: () => void;
}

const Tooltip: React.FC<TooltipProps> = ({ lane, anchorRef, onClose }) => {
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !tooltipRef.current) {
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const tooltipRect = tooltipRef.current.getBoundingClientRect();
    setPos(computeFloatingPosition(anchorRect, tooltipRect.width, tooltipRect.height));
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition, lane]);

  useEffect(() => {
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (
        tooltipRef.current &&
        !tooltipRef.current.contains(e.target as Node) &&
        !anchorRef.current?.contains(e.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose, anchorRef]);

  return createPortal(
    <div
      ref={tooltipRef}
      className="dispatch-tooltip"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      <div className="dispatch-tooltip__header">
        <div className="dispatch-tooltip__title">{lane.destination}</div>
        <div className="dispatch-tooltip__subtitle">
          {lane.origin} → {lane.destination}
        </div>
      </div>
      <div className="dispatch-tooltip__body">
        <div className="dispatch-tooltip__row">
          <span className="dispatch-tooltip__label">Departure</span>
          <span className="dispatch-tooltip__value">{lane.departure}</span>
        </div>
        <div className="dispatch-tooltip__row">
          <span className="dispatch-tooltip__label">Carrier</span>
          <span className="dispatch-tooltip__value">{lane.carrier}</span>
        </div>
        <div className="dispatch-tooltip__row">
          <span className="dispatch-tooltip__label">Drops</span>
          <span className="dispatch-tooltip__value">{lane.drops}</span>
        </div>
      </div>
    </div>,
    document.body
  );
};

// ─── Context Menu ─────────────────────────────────────────────────────────────

interface ContextMenuProps {
  anchorRef: React.RefObject<HTMLButtonElement | null>;
  onClose: () => void;
  onViewLaneDetails: () => void;
  onEditLane: () => void;
  onDuplicateLane: () => void;
  onMoveToLane: () => void;
  onAddDrop: () => void;
  onRemoveDispatch: () => void;
}

const ContextMenu: React.FC<ContextMenuProps> = ({ anchorRef, onClose, onViewLaneDetails, onEditLane, onDuplicateLane, onMoveToLane, onAddDrop, onRemoveDispatch }) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);

  const updatePosition = useCallback(() => {
    if (!anchorRef.current || !menuRef.current) {
      return;
    }

    const anchorRect = anchorRef.current.getBoundingClientRect();
    const menuRect = menuRef.current.getBoundingClientRect();
    const { top } = computeFloatingPosition(anchorRect, menuRect.width, menuRect.height);

    let left = anchorRect.left - menuRect.width + anchorRect.width;
    if (left + menuRect.width > window.innerWidth - VIEWPORT_PADDING) {
      left = window.innerWidth - menuRect.width - VIEWPORT_PADDING;
    }
    left = Math.max(VIEWPORT_PADDING, left);

    setPos({ top, left });
  }, [anchorRef]);

  useLayoutEffect(() => {
    updatePosition();
  }, [updatePosition]);

  useEffect(() => {
    window.addEventListener("scroll", updatePosition, true);
    window.addEventListener("resize", updatePosition);

    return () => {
      window.removeEventListener("scroll", updatePosition, true);
      window.removeEventListener("resize", updatePosition);
    };
  }, [updatePosition]);

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [onClose]);

  const handleViewLaneDetails = () => {
    onViewLaneDetails();
    onClose();
  };

  const handleEditLane = () => {
    onEditLane();
    onClose();
  };

  const handleDuplicateLane = () => {
    onDuplicateLane();
    onClose();
  };

  const handleMoveToLane = () => {
    onMoveToLane();
    onClose();
  };

  const handleAddDrop = () => {
    onAddDrop();
    onClose();
  };

  const handleRemoveDispatch = () => {
    onRemoveDispatch();
    onClose();
  };

  const items = [
    { label: "View Lane Details", danger: false, onClick: handleViewLaneDetails },
    { label: "Edit Lane", danger: false, onClick: handleEditLane },
    { label: "Duplicate", danger: false, onClick: handleDuplicateLane },
    { label: "Move to", danger: false, onClick: handleMoveToLane },
    { label: "Add Drop", danger: false, onClick: handleAddDrop },
    { label: "Remove Dispatch", danger: true, onClick: handleRemoveDispatch },
  ];

  return createPortal(
    <div
      ref={menuRef}
      className="dispatch-context-menu"
      style={{
        top: pos?.top ?? -9999,
        left: pos?.left ?? 0,
        visibility: pos ? "visible" : "hidden",
      }}
    >
      {items.map((item, i) => (
        <React.Fragment key={item.label}>
          {i === items.length - 1 && (
            <div className="dispatch-context-menu__divider" />
          )}
          <button
            className={`dispatch-context-menu__item${item.danger ? " dispatch-context-menu__item--danger" : ""}`}
            onClick={item.onClick}
          >
            {item.label}
          </button>
        </React.Fragment>
      ))}
    </div>,
    document.body
  );
};

// ─── Dispatch Chip ────────────────────────────────────────────────────────────

interface DispatchChipProps {
  dispatch: Dispatch;
  rowLabel: string;
  refetchLanesByDay: () => void;
}

const DispatchChip: React.FC<DispatchChipProps> = ({ dispatch, rowLabel, refetchLanesByDay }) => {
  const staticLoadState = useSelector(staticLoadSelector);
  const [showTooltip, setShowTooltip] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [isOpenUpdateSidebar, setIsOpenUpdateSidebar] = useState<boolean>(false);
  const [isOpenCopyMoveLaneSidebar, setIsOpenCopyMoveLaneSidebar] = useState<boolean>(false);
  const [isEditMode, setIsEditMode] = useState<'view' | 'edit' | 'create'>('view');
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
  const [copyType, setCopyType] = useState<'copy' | 'move'>('copy');
  const deleteLaneMutation = useDeleteLane();
  const copyLaneToDayMutation = useCopyLaneToDay();

  const dispatchEffect = useDispatch();
  const { data: laneByPlanId, isLoading: isLoadingLaneByPlanID, refetch: refetchLaneByPlanID } = useLaneByPlanID(
    staticLoadState.selectedPlanId, staticLoadState.selectedDivision, staticLoadState.weekStartDate)

  const terminalNumbers = useMemo(() => {
    if (!isOpenUpdateSidebar || staticLoadState.selectedPlanId !== dispatch.id) return [];
    return laneByPlanId?.drops?.map((d) => d.terminalNumber?.toString()).filter((n) => n != null && n !== "") ?? [];
  }, [isOpenUpdateSidebar, staticLoadState.selectedPlanId, dispatch.id, laneByPlanId?.drops]);

  const { data: terminalDetails } = useTerminalDetailsByNumbers(terminalNumbers);
  const terminalMap = useMemo(
    () => formatTerminalsByNumber(terminalDetails),
    [terminalDetails]
  );

  const [isModalOpen, setIsModalOpen] = useState(false);

  const chipRef = useRef<HTMLDivElement>(null);
  const menuBtnRef = useRef<HTMLButtonElement>(null);

  const lane: Lane = {
    origin: dispatch.origin ?? rowLabel,
    destination: dispatch.destinationName ?? dispatch.airport,
    departure: dispatch.time,
    carrier: dispatch.carrier ?? "—",
    drops: dispatch.drops ?? 0,
  };

  const selectedPlanId = useCallback(() => { dispatchEffect(setPlanId(dispatch.id)) }, [dispatchEffect, dispatch.id]);

  const handleConfirmDelete = () => {
    deleteLaneMutation.mutate(dispatch.id, {
      onSuccess: () => {
        refetchLanesByDay();
        setShowDeleteModal(false);
        setShowMenu(false);
      },
    });
  };

  const carrierRate = useMemo(() => {
    if (!dispatch.carrierRate) return "";
    return dispatch.carrierRate.replace("$", "");
  }, [dispatch.carrierRate]);

  return (
    <>
      <div
        ref={chipRef}
        className={`dispatch-chip dispatch-chip--${dispatch.color ?? "blue"}`}
        onClick={() => setShowTooltip((v) => !v)}
      >

        <span className="dispatch-chip__airport" title={dispatch.destinationName ?? dispatch.airport}>
          {dispatch.airport}
        </span>
        <span className="dispatch-chip__time">{dispatch.time}</span>

        {dispatch.carrier && (
          <span className="dispatch-chip__carrier" title={dispatch.carrier}>{dispatch.carrier}</span>
        )}
        {carrierRate && (
          <span className="dispatch-chip__price">${carrierRate}</span>
        )}

        <button
          ref={menuBtnRef}
          className="dispatch-chip__more"
          onClick={(e) => {
            e.stopPropagation();
            setShowTooltip(false);
            setShowMenu((v) => !v);
          }}
          aria-label="More options"
        >
          <ThreeDotsIcon />
        </button>
      </div>

      {showTooltip && (
        <Tooltip
          lane={lane}
          anchorRef={chipRef}
          onClose={() => setShowTooltip(false)}
        />
      )}
      {showMenu && (
        <ContextMenu
          anchorRef={menuBtnRef}
          onClose={() => setShowMenu(false)}
          onViewLaneDetails={() => { setIsOpenUpdateSidebar(true); setIsEditMode('view'); selectedPlanId() }}
          onEditLane={() => { setIsOpenUpdateSidebar(true); setIsEditMode('edit'); selectedPlanId() }}
          onDuplicateLane={() => { setIsOpenCopyMoveLaneSidebar(true); setCopyType('copy'); setIsOpenUpdateSidebar(false); selectedPlanId(); }}
          onMoveToLane={() => { setIsOpenCopyMoveLaneSidebar(true); setCopyType('move'); setIsOpenUpdateSidebar(false); selectedPlanId(); }}
          onAddDrop={() => { setIsModalOpen(true); selectedPlanId(); }}
          onRemoveDispatch={() => { setShowDeleteModal(true); selectedPlanId() }}
        />
      )}
      <ConfirmationModal
        onCancel={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmDelete}
        opened={!!showDeleteModal}
        spinner={deleteLaneMutation.isLoading}
      >
        <div className="confirmation-modal__content">
          <AlertIcon />
          <div>Are you sure you want to delete this dispatch?</div>
        </div>
      </ConfirmationModal>
      <UpdateSidebar
        isOpen={isOpenUpdateSidebar}
        onClose={() => {
          setIsOpenUpdateSidebar(false);
          setShowMenu(false);
        }}
        title={isEditMode === 'edit' ? "Edit Lane" : isEditMode === 'create' ? "Create Lane" : "Lane Details"}
        data={laneByPlanId || { lane: {}, drops: [] }}
        isLoading={isLoadingLaneByPlanID}
        terminalMap={terminalMap}
        isEditMode={isEditMode}
        refetchLane={refetchLaneByPlanID}
        refetchLaneByDay={refetchLanesByDay}
        handleAddDrop={() => { }}
      />
      <CopyMoveLaneSidebar
        isOpen={isOpenCopyMoveLaneSidebar}
        onClose={() => {
          setIsOpenCopyMoveLaneSidebar(false);
          setShowMenu(false);
        }}
        isLoading={false}
        copyLaneToDayMutation={copyLaneToDayMutation}
        type={copyType}
      />
      <AddDropModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={() => {
          refetchLaneByPlanID();
          refetchLanesByDay();
        }}
      />
    </>
  );
};

// ─── Board Header ─────────────────────────────────────────────────────────────

interface BoardHeaderProps {
  currentDate: moment.Moment | null;
  weekStartDate: string;
  onPrevDay: () => void;
  onNextDay: () => void;
}

const BoardHeader: React.FC<BoardHeaderProps> = ({
  currentDate,
  weekStartDate,
  onPrevDay,
  onNextDay,
}) => {
  const weekStart = moment(weekStartDate, "YYYY-MM-DD", true);
  const selectedDay = currentDate?.isValid() ? currentDate : null;
  const hasValidWeek = weekStart.isValid() && !!selectedDay;
  const weekEnd = weekStart.clone().add(6, "days");
  const isAtWeekStart = !!selectedDay?.isSameOrBefore(weekStart, "day");
  const isAtWeekEnd = !!selectedDay?.isSameOrAfter(weekEnd, "day");
  const selectedDayLabel = selectedDay ? selectedDay.format("dddd, MMMM D") : "";

  return (
    <div className="dispatch-board__header">
      <div className="dispatch-board__origin-cell">
        <span className="dispatch-board__origin-label">Origin</span>
      </div>

      <div className="dispatch-board__day-columns">
        {Array.from({ length: DAY_COLUMN_COUNT }, (_, index) => (
          <div
            key={index}
            className={`dispatch-board__day-column${index === 0 ? "" : " dispatch-board__day-column--empty"}`}
          >
            {index === 0 && (
              <div className="dispatch-board__date-nav">
                <button
                  type="button"
                  className="dispatch-board__nav-btn"
                  onClick={onPrevDay}
                  disabled={!hasValidWeek || isAtWeekStart}
                  aria-label="Previous day"
                >
                  <ArrowLeftIcon />
                </button>
                <span className="dispatch-board__date">{selectedDayLabel}</span>
                <button
                  type="button"
                  className="dispatch-board__nav-btn"
                  onClick={onNextDay}
                  disabled={!hasValidWeek || isAtWeekEnd}
                  aria-label="Next day"
                >
                  <ArrowRightIcon />
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

// ─── Add Dispatch Cell ────────────────────────────────────────────────────────

interface AddDispatchContext {
  dayOfWeek: number;
  originAirportCode: string;
  originName: string;
}

const AddDispatchCell: React.FC<{ onAddDispatch: () => void }> = ({ onAddDispatch }) => (
  <button type="button" className="dispatch-add-cell" onClick={onAddDispatch}>+ Add Dispatch</button>
);


// ─── Main Component ───────────────────────────────────────────────────────────

const parseWeekStart = (weekStartDate: string) =>
  moment(weekStartDate, "YYYY-MM-DD", true);

const DispatchBoard: React.FC = () => {
  const staticLoadState = useSelector(staticLoadSelector);
  const weekStart = useMemo(
    () => parseWeekStart(staticLoadState.weekStartDate),
    [staticLoadState.weekStartDate]
  );
  const [currentDate, setCurrentDate] = useState<moment.Moment | null>(() =>
    weekStart.isValid() ? weekStart.clone() : null
  );
  const [isOpenAddDispatchModal, setIsOpenAddDispatchModal] = useState<boolean>(false);
  const [addDispatchContext, setAddDispatchContext] = useState<AddDispatchContext | null>(null);
  // API / weekOptions use Monday=1 … Sunday=7 (moment isoWeekday)
  const dayOfWeek = currentDate?.isValid() ? currentDate.isoWeekday() : 1;

  const { data: weeks } = useWeeks();

  const currentWeek = useMemo(
    () => weeks?.find((w) => w.weekStartDate === staticLoadState.weekStartDate),
    [weeks, staticLoadState.weekStartDate]
  );

  const canAddDispatch =
    currentWeek?.status === "ACTIVE" &&
    currentWeek?.scheduleType === "WORKING";

  const { data: lanesByDay, isLoading: isLoadingLanesByDay, refetch: refetchLanesByDay } = useLanesByDay(
    staticLoadState.selectedDivision,
    weekStart.isValid() ? weekStart.format("YYYY-MM-DD") : "",
  );

  const rows = useMemo(
    () => transformLanesToRows(lanesByDay, dayOfWeek),
    [lanesByDay, dayOfWeek]
  );

  useEffect(() => {
    const nextWeekStart = parseWeekStart(staticLoadState.weekStartDate);
    setCurrentDate(nextWeekStart.isValid() ? nextWeekStart.clone() : null);
  }, [staticLoadState.weekStartDate]);

  const prevDay = () => setCurrentDate((d) => (d ? d.clone().subtract(1, "day") : d));
  const nextDay = () => setCurrentDate((d) => (d ? d.clone().add(1, "day") : d));

  const handleOpenAddDispatch = useCallback((row: Row) => {
    setAddDispatchContext({
      dayOfWeek,
      originAirportCode: row.originCode,
      originName: row.label,
    });
    setIsOpenAddDispatchModal(true);
  }, [dayOfWeek]);

  const handleCloseAddDispatch = useCallback(() => {
    setIsOpenAddDispatchModal(false);
    setAddDispatchContext(null);
  }, []);

  return (
    <div className="dispatch-board">
      <BoardHeader
        currentDate={currentDate}
        weekStartDate={staticLoadState.weekStartDate}
        onPrevDay={prevDay}
        onNextDay={nextDay}
      />
      {/* Rows */}
      <div className="dispatch-board__body">
        {isLoadingLanesByDay ? (
          <div className="dispatch-board__loading">
            <Loading isLoading={isLoadingLanesByDay} />
          </div>
        ) : (rows.length === 0 ? (
          <div className="dispatch-board__empty">No dispatches</div>
        ) : (
          rows.map((row, rowIdx) => (
            <div key={rowIdx} className="dispatch-board__row">
              <div className="dispatch-board__row-label" title={row.label}>{row.label}</div>
              <div className="dispatch-board__chips">
                {row.dispatches.map((dispatch, colIdx) =>
                  dispatch ? (
                    <DispatchChip
                      key={dispatch.id}
                      dispatch={dispatch}
                      rowLabel={row.label}
                      refetchLanesByDay={refetchLanesByDay}
                    />
                  ) : (
                    <div key={`empty-${colIdx}`} className="dispatch-chip-gap" />
                  )
                )}
                {canAddDispatch && (
                  <AddDispatchCell onAddDispatch={() => handleOpenAddDispatch(row)} />
                )}
              </div>
            </div>
          ))
        ))}
      </div>
      <UpdateSidebar
        isOpen={isOpenAddDispatchModal}
        onClose={handleCloseAddDispatch}
        title="Add Dispatch"
        data={{
          lane: {
            dayOfWeek: addDispatchContext?.dayOfWeek ?? dayOfWeek,
            originAirportCode: addDispatchContext?.originAirportCode,
            originName: addDispatchContext?.originName,
          },
          drops: [],
        }}
        isLoading={false}
        terminalMap={{}}
        isEditMode={'add-dispatch'}
        refetchLane={() => { }}
        refetchLaneByDay={refetchLanesByDay}
        handleAddDrop={() => { }}
      />
    </div>
  );
};

export default DispatchBoard;